import { FormFieldType, type FormOption } from '../../common/types/form/form-fields';
import { type OptionsValue, OTHER_ID } from '../../common/types/form/form-fields/field-values';
import type { ApiFormChoicesResponse } from '../types/form-choices';
import type { FormChoice, FormQuestion, FormUser } from '../types/proforma-form';

export const convertFormChoice = (choice: FormChoice): FormOption => ({
	id: choice.id,
	label: choice.label,
	...(choice.children && {
		children: choice.children.map((child) => convertFormChoice(child)),
	}),
});

export const findChoice = (
	choices: FormChoice[],
	choiceId: string,
	isCascading?: boolean,
): FormChoice => {
	// some Jira fields (non-cascading) have ID with colon in it as well
	if (isCascading && choiceId.includes(':')) {
		const [parentId, childId] = choiceId.split(':');
		const parent = choices.find((c) => c.id === parentId);
		const child = parent?.children?.find((c) => c.id === childId);
		return child
			? {
					id: choiceId,
					label: `${parent?.label || parentId} - ${child.label}`,
				}
			: {
					id: parentId,
					label: parent?.label || parentId,
				};
	}

	const choiceLabel = choices.find((choice) => choice.id === choiceId)?.label;
	return {
		id: choiceId,
		label: choiceLabel || choiceId,
	};
};

/**
 * Returns the linked choices for a question from the formChoices API response.
 *
 * Returns undefined (not []) when formChoices is not provided
 * This distinction is important: undefined signals "choices not loaded" and allows convertQuestion
 * to fall back to question.choices (the snapshot stored in the form design). An empty array []
 * would mean "loaded but no choices exist", which would incorrectly result in empty options.
 *
 * Consumers that load formChoices will get the live linked choices.
 * e.g.
 * - submitted form in issue-view loading formChoices with `allowSubmittedForm=true`,
 *   - REQIN-1186
 *   - https://hello.atlassian.net/wiki/spaces/FORMS/pages/4017333972/Handle+editing+of+forms+once+submitted
 * - open form in issue-view
 * - form in request-create
 *
 * Consumers that skip formChoices, will get undefined, triggering fallback to question.choices
 * - submitted forms in portal-view
 */
export const getLinkedChoices = (
	question: FormQuestion,
	formChoices?: ApiFormChoicesResponse,
): FormChoice[] | undefined => {
	if (question.jiraField) {
		return formChoices?.fields?.[question.jiraField];
	}
	if (question.dcId) {
		return formChoices?.dataConnections?.[question.dcId];
	}
	return undefined;
};

export const getChoiceValue = (
	choiceIds: string[],
	hasOtherOption: boolean,
	question: FormQuestion,
	fieldType: FormFieldType,
	linkedChoices?: FormChoice[],
	otherOptionText?: string,
): OptionsValue | undefined => {
	const notOtherSelectedChoiceIds = choiceIds.filter((choice) =>
		hasOtherOption ? choice !== OTHER_ID : true,
	);
	const otherChoiceSelected = hasOtherOption && !linkedChoices && choiceIds.includes(OTHER_ID);
	if (notOtherSelectedChoiceIds.length < 1 && !otherChoiceSelected) {
		return undefined;
	}
	return {
		options: notOtherSelectedChoiceIds.map((choice) =>
			findChoice(
				linkedChoices || question.choices || [],
				choice,
				fieldType === FormFieldType.CASCADING_SELECT,
			),
		),
		...(otherChoiceSelected && { otherOptionText: otherOptionText ?? '' }),
	};
};

export const getUserChoiceValue = (users: FormUser[]): OptionsValue => ({
	options: users.map((user) => ({
		id: user.id,
		label: user.name,
	})),
});
